import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { GolfHoleByHole } from '@/components/GolfHoleByHole/GolfHoleByHole'
import { GolfScorecardHeader } from '@/components/GolfScorecardHeader/GolfScorecardHeader'
import { STORE_STATUS } from '@/constants/store'
import { useLeaderboardStore } from '@/stores/golfPricing/leaderboard.store'
import { useTeamHoleByHoleStore } from '@/stores/golfPricing/teamHoleByHole.store'
import { useTeamRoundStore } from '@/stores/golfPricing/teamRound.store'
import type { TeamRound } from '@/types/dde/teamRound.types'

import { ErrorState } from './components/ErrorState'
import { LoadingState } from './components/LoadingState'
import { calculateRoundsInfo, findTeamRoundDetail } from './utils'

export const TeamViewDDE = () => {
  const { tournamentId, teamNo } = useParams<{ tournamentId: string; teamNo: string }>()

  const tournamentIdNum = tournamentId ? Number(tournamentId) : null
  const teamNoNum = teamNo ? Number(teamNo) : null

  const {
    data: holeByHoleData,
    status,
    error,
    wsError,
    isConnecting,
    connectWebSocket,
    disconnectWebSocket
  } = useTeamHoleByHoleStore(
    useShallow((state) => ({
      data: state.data,
      status: state.status,
      error: state.error,
      wsError: state.wsError,
      isConnecting: state.isConnecting,
      connectWebSocket: state.connectWebSocket,
      disconnectWebSocket: state.disconnectWebSocket
    }))
  )

  const {
    data: leaderboardData,
    connectWebSocket: connectLeaderboard,
    disconnectWebSocket: disconnectLeaderboard
  } = useLeaderboardStore(
    useShallow((state) => ({
      data: state.data,
      connectWebSocket: state.connectWebSocket,
      disconnectWebSocket: state.disconnectWebSocket
    }))
  )

  const {
    data: teamRoundData,
    connectWebSocket: connectTeamRound,
    disconnectWebSocket: disconnectTeamRound
  } = useTeamRoundStore(
    useShallow((state) => ({
      data: state.data,
      connectWebSocket: state.connectWebSocket,
      disconnectWebSocket: state.disconnectWebSocket
    }))
  )

  useEffect(() => {
    if (tournamentIdNum && !Number.isNaN(tournamentIdNum)) {
      connectLeaderboard(tournamentIdNum)
    }
    return () => {
      disconnectLeaderboard()
    }
  }, [tournamentIdNum, connectLeaderboard, disconnectLeaderboard])

  useEffect(() => {
    if (
      tournamentIdNum &&
      !Number.isNaN(tournamentIdNum) &&
      teamNoNum &&
      !Number.isNaN(teamNoNum)
    ) {
      connectWebSocket({ tournamentId: tournamentIdNum, teamNo: teamNoNum })
    }
    return () => {
      disconnectWebSocket()
    }
  }, [tournamentIdNum, teamNoNum, connectWebSocket, disconnectWebSocket])

  const teamStanding = useMemo(() => {
    if (!leaderboardData || !teamNoNum) return undefined
    return leaderboardData.find((row) => row.teamNo === teamNoNum)?.rawStanding
  }, [leaderboardData, teamNoNum])

  const { availableRounds, defaultRound, hasPlayoff } = useMemo(
    () => calculateRoundsInfo(holeByHoleData[0], teamStanding),
    [holeByHoleData, teamStanding]
  )

  const [selectedRound, setSelectedRound] = useState<number | string>(defaultRound)

  useEffect(() => {
    setSelectedRound(defaultRound)
  }, [defaultRound])

  useEffect(() => {
    if (
      tournamentIdNum &&
      !Number.isNaN(tournamentIdNum) &&
      teamNoNum &&
      !Number.isNaN(teamNoNum) &&
      selectedRound
    ) {
      const roundNo = typeof selectedRound === 'string' ? Number(selectedRound) : selectedRound
      if (!Number.isNaN(roundNo)) {
        connectTeamRound({ tournamentId: tournamentIdNum, teamNo: teamNoNum, roundNo })
      }
    }
    return () => {
      disconnectTeamRound()
    }
  }, [tournamentIdNum, teamNoNum, selectedRound, connectTeamRound, disconnectTeamRound])

  const teamRound = useMemo((): TeamRound[] => {
    if (!teamRoundData || teamRoundData.length === 0) return []
    return teamRoundData as unknown as TeamRound[]
  }, [teamRoundData])

  const teamRoundHeader = useMemo(
    () => findTeamRoundDetail(teamRound, selectedRound, teamStanding),
    [teamRound, selectedRound, teamStanding]
  )

  const isLoading = isConnecting && holeByHoleData.length === 0
  const displayError = wsError ?? (status === STORE_STATUS.error ? error : null)
  const shouldShowError = displayError && !isConnecting && holeByHoleData.length === 0

  if (shouldShowError) {
    return <ErrorState error={displayError} />
  }

  if (isLoading) {
    return <LoadingState />
  }

  const roundScores = holeByHoleData[0]?.roundScores || {}

  return (
    <>
      {teamRoundHeader && tournamentId && (
        <GolfScorecardHeader
          teamRound={teamRoundHeader}
          tournamentId={tournamentId}
          teamStanding={teamStanding}
          availableRounds={availableRounds}
          hasPlayoff={hasPlayoff}
          selectedRound={selectedRound}
          onRoundChange={setSelectedRound}
        />
      )}
      <GolfHoleByHole
        roundScores={roundScores}
        currentRound={typeof selectedRound === 'string' ? Number(selectedRound) : selectedRound}
      />
    </>
  )
}
